import { Component, Inject, InjectionToken, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import 'zone.js';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { MessagingService } from './services/messaging.service';
import { environment } from '../environments/environment';
import { CommonService } from './services/common.service';
import { assets } from './core/constants/assets.const';
import { Title } from '@angular/platform-browser';

declare global {
  interface Window {
    gapiLoaded: () => void;
  }
}

@Component({
  selector: 'mindbase-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, TranslateModule, HttpClientModule, NgxUiLoaderModule],
  providers: [TranslateService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  protected title = '';     
  protected selectedLanguage: string | null = null;

  
  constructor(public translate: TranslateService, private titleService: Title, private router: Router, private _messagingService: MessagingService,
    private _commonService: CommonService,
      @Inject(PLATFORM_ID) private platformId: InjectionToken<object>
  ) {   
    translate.addLangs(['en', 'ar']);
    if (isPlatformBrowser(this.platformId)) {
      this.selectedLanguage = localStorage.getItem(environment.langKey) ? localStorage.getItem(environment.langKey) : 'en';
      this._commonService.selectedLanguage.next(
        this.selectedLanguage ? this.selectedLanguage : 'en'
      );
      this.translate.setDefaultLang(this.selectedLanguage ? this.selectedLanguage : 'en');
      document.documentElement.lang = this.selectedLanguage ? this.selectedLanguage : 'en';

      // Add or remove the 'rtl' class based on the selected language
      this.toggleBodyClass(this.selectedLanguage ? this.selectedLanguage : '');

      // Listen for language change
      this._commonService.selectedLanguage.subscribe((lang: any) => {
        if (lang && lang !== this.selectedLanguage) {
          this.selectedLanguage = lang;
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(environment.langKey, lang);
            this.toggleBodyClass(lang);
            window.location.reload();
          }
        }
      });
    }
  }
  
  ngOnInit(): void {
    setTimeout(() => {      
      this.title = this.getWebSettings()?.headerTitle || '';
      this.titleService.setTitle(this.title);
      const favicon = this.getWebSettings()?.favIcon || '';    
      this.updateFavicon(favicon);
    }, 1000);
    if (isPlatformBrowser(this.platformId)) {
      // this._messagingService.requestPermission();
      // this._messagingService.listenForFrontMessages();
      this.translate.get('notificationAlert').subscribe(() => {
        if (Notification.permission === 'denied') {
          alert(this.translate.instant('Please enable browser notifications to receive important updates.'));
        } 
      });
    }

        
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.action === 'navigate') {
        window.location.href = event.data.url;
      }  else if (event.data && event.data.action === 'playNotificationSound') {
        this.playNotificationSound();
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        document.body.scrollTop = 0;
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }
    });
  }

  getWebSettings(): any {
    const val = localStorage.getItem('webSettings');
    if (!val || val === 'undefined') return {};
    try {
      return JSON.parse(val);
    } catch {
      return {};
    }
  }

  updateFavicon(faviconUrl: string): void {
    const links = document.querySelectorAll('link[rel*=\'icon\']');
    links.forEach(link => link.parentNode?.removeChild(link));

    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.type = 'image/webp'; // adjust to your file type if needed
    newLink.href = faviconUrl;
    document.head.appendChild(newLink);
  }

  private playNotificationSound(): void {
    const audio = new Audio(assets.notificationSound); // Adjust path as needed
    audio.play().catch((error) => {
      console.error('Error playing notification sound:', error);
    });
  }

  private toggleBodyClass(lang: string): void {
    const body = document.body;
    if (lang === 'ar') {
      body.classList.add('rtl');
    } else {
      body.classList.remove('rtl');
    }
  }
}
